/**
 * DocumentToolbar - Left toolbar with document-specific tools
 * Pattern follows PresentationToolbar.tsx
 */

import { Plus, Type, Flag, ZoomIn, ZoomOut, Undo, Redo, Maximize2 } from 'lucide-react'
import { TooltipProvider } from '@/components/ui/Tooltip'
import { ToolbarButton } from '@/components/ui/ToolbarButton'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuLabel,
} from '@/components/ui/DropdownMenu'
import type { DocumentCanvasPreset } from '@/types/BlockProperties'
import { DOCUMENT_SIZES } from './DocumentCanvas'
import './document-toolbar.css'
import '@/components/toolbar/toolbar.css'

interface DocumentToolbarProps {
	canUndo: boolean
	canRedo: boolean
	onUndo: () => void
	onRedo: () => void
	onAddSection: () => void
	onZoomIn: () => void
	onZoomOut: () => void
	onToggleFlag: () => void
	isFlagMode: boolean
	canvasPreset: DocumentCanvasPreset
	onChangeSize: (preset: DocumentCanvasPreset) => void
}

export function DocumentToolbar({
	canUndo,
	canRedo,
	onUndo,
	onRedo,
	onAddSection,
	onZoomIn,
	onZoomOut,
	onToggleFlag,
	isFlagMode,
	canvasPreset,
	onChangeSize,
}: DocumentToolbarProps) {
	return (
		<TooltipProvider>
			<aside className="document-toolbar">
				<div className="document-toolbar__tools">
					{/* Add Section */}
					<ToolbarButton
						icon={Plus}
						tooltip="Add Section"
						onClick={onAddSection}
						className="document-toolbar__button"
					/>

					{/* Text Tool (placeholder) */}
					<ToolbarButton
						icon={Type}
						tooltip="Add Text (coming soon)"
						onClick={() => console.log('Text tool - coming soon')}
						className="document-toolbar__button"
						disabled
					/>

					{/* Wristband Flag Tool */}
					<ToolbarButton
						icon={Flag}
						tooltip="Wristband Flag"
						isActive={isFlagMode}
						onClick={onToggleFlag}
						className="document-toolbar__button"
					/>

					{/* Document Size Selector */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<ToolbarButton
								icon={Maximize2}
								tooltip="Document Size"
								className="document-toolbar__button"
							/>
						</DropdownMenuTrigger>
						<DropdownMenuContent side="right" align="start">
							<DropdownMenuLabel>Document Size</DropdownMenuLabel>
							<DropdownMenuRadioGroup value={canvasPreset} onValueChange={onChangeSize as (value: string) => void}>
								<DropdownMenuRadioItem value="letter">
									{DOCUMENT_SIZES.letter.label}
								</DropdownMenuRadioItem>
								<DropdownMenuRadioItem value="legal">
									{DOCUMENT_SIZES.legal.label}
								</DropdownMenuRadioItem>
								<DropdownMenuRadioItem value="half_letter">
									{DOCUMENT_SIZES.half_letter.label}
								</DropdownMenuRadioItem>
								<DropdownMenuRadioItem value="wristband">
									{DOCUMENT_SIZES.wristband.label}
								</DropdownMenuRadioItem>
							</DropdownMenuRadioGroup>
						</DropdownMenuContent>
					</DropdownMenu>

					{/* Zoom In */}
					<ToolbarButton
						icon={ZoomIn}
						tooltip="Zoom In"
						onClick={onZoomIn}
						className="document-toolbar__button"
					/>

					{/* Zoom Out */}
					<ToolbarButton
						icon={ZoomOut}
						tooltip="Zoom Out"
						onClick={onZoomOut}
						className="document-toolbar__button"
					/>

					{/* Undo */}
					<ToolbarButton
						icon={Undo}
						tooltip="Undo (Cmd+Z)"
						disabled={!canUndo}
						onClick={onUndo}
						className="document-toolbar__button"
					/>

					{/* Redo */}
					<ToolbarButton
						icon={Redo}
						tooltip="Redo (Cmd+Shift+Z)"
						disabled={!canRedo}
						onClick={onRedo}
						className="document-toolbar__button"
					/>
				</div>
			</aside>
		</TooltipProvider>
	)
}
